import React, { useEffect, useRef, useState } from 'react';
import * as pdfjsLib from 'pdfjs-dist';
import { 
  ChevronLeft, 
  ChevronRight, 
  ZoomIn, 
  ZoomOut, 
  RotateCw, 
  Download, 
  ExternalLink, 
  FileText, 
  AlertCircle, 
  Loader2,
  HardDrive
} from 'lucide-react';

// Configure pdfjs worker
pdfjsLib.GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/pdf.worker.min.mjs`;

// Helper utilities for Google Drive & External URLs
export function extractGoogleDriveId(url: string): string | null {
  if (!url) return null;
  const trimmed = url.trim();
  const matchD = trimmed.match(/\/(?:file\/|document\/|presentation\/|spreadsheets\/)?d\/([a-zA-Z0-9_-]+)/);
  if (matchD && matchD[1]) return matchD[1];
  const matchId = trimmed.match(/[?&]id=([a-zA-Z0-9_-]+)/);
  if (matchId && matchId[1]) return matchId[1];
  return null;
}

export function isGoogleDriveUrl(url: string): boolean {
  if (!url) return false;
  return (
    url.includes('drive.google.com') ||
    url.includes('docs.google.com') ||
    url.includes('googleusercontent.com')
  );
}

export function getGoogleDrivePreviewUrl(url: string): string | null {
  const fileId = extractGoogleDriveId(url);
  if (fileId) {
    return `https://drive.google.com/file/d/${fileId}/preview`;
  }
  return null;
}

export function getGoogleDriveDownloadUrl(url: string): string {
  const fileId = extractGoogleDriveId(url);
  if (fileId) {
    return `https://drive.google.com/uc?export=download&id=${fileId}`;
  }
  return url;
}

interface PdfViewerProps {
  url: string;
  title?: string;
  className?: string;
  height?: string;
}

export const PdfViewer: React.FC<PdfViewerProps> = ({ 
  url, 
  title = 'Dokumen PDF', 
  className = '',
  height = '600px'
}) => {
  const [numPages, setNumPages] = useState<number>(0);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [scale, setScale] = useState<number>(1.2);
  const [rotation, setRotation] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(true);
  const [useFallbackIframe, setUseFallbackIframe] = useState<boolean>(false);
  const [fallbackIframeUrl, setFallbackIframeUrl] = useState<string>('');

  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const renderTaskRef = useRef<any>(null);

  const isGDrive = isGoogleDriveUrl(url);
  const gDriveId = extractGoogleDriveId(url);
  const gDriveEmbedUrl = gDriveId ? `https://drive.google.com/file/d/${gDriveId}/preview` : null;
  const gDriveViewUrl = gDriveId ? `https://drive.google.com/file/d/${gDriveId}/view` : url;
  const gDriveDownloadUrl = gDriveId ? `https://drive.google.com/uc?export=download&id=${gDriveId}` : url;

  // Load PDF Document
  useEffect(() => {
    let isMounted = true;
    setLoading(true);
    setUseFallbackIframe(false);
    setCurrentPage(1);

    // If it's a Google Drive URL, directly use Google Drive preview iframe!
    if (isGDrive && gDriveEmbedUrl) {
      setUseFallbackIframe(true);
      setFallbackIframeUrl(gDriveEmbedUrl);
      setLoading(false);
      return;
    }

    // Otherwise try PDF.js canvas rendering
    const loadPdf = async () => {
      try {
        const loadingTask = pdfjsLib.getDocument(url);
        const pdf = await loadingTask.promise;
        if (!isMounted) return;

        setNumPages(pdf.numPages);
        setLoading(false);
        renderPage(pdf, 1, scale, rotation);
      } catch (err: any) {
        console.warn('PDF.js rendering error, using fallback preview viewer:', err);
        if (isMounted) {
          setLoading(false);
          setUseFallbackIframe(true);
          
          if (url.startsWith('http://') || url.startsWith('https://')) {
            // External HTTP/HTTPS URL fallback via Google Docs Viewer
            setFallbackIframeUrl(`https://docs.google.com/gview?url=${encodeURIComponent(url)}&embedded=true`);
          } else {
            setFallbackIframeUrl(url);
          }
        }
      }
    };

    loadPdf();

    return () => {
      isMounted = false;
      if (renderTaskRef.current) {
        try {
          renderTaskRef.current.cancel();
        } catch (e) {
          // ignore
        }
      }
    };
  }, [url]);

  // Re-render page on page, scale, or rotation change
  const renderPage = async (pdfDoc?: any, pageNum: number = currentPage, currentScale: number = scale, currentRotation: number = rotation) => {
    try {
      let pdf = pdfDoc;
      if (!pdf) {
        const loadingTask = pdfjsLib.getDocument(url);
        pdf = await loadingTask.promise;
      }

      const page = await pdf.getPage(pageNum);
      const canvas = canvasRef.current;
      if (!canvas) return;

      const viewport = page.getViewport({ scale: currentScale, rotation: currentRotation });
      const context = canvas.getContext('2d');
      if (!context) return;

      const outputScale = window.devicePixelRatio || 1;
      canvas.width = Math.floor(viewport.width * outputScale);
      canvas.height = Math.floor(viewport.height * outputScale);
      canvas.style.width = `${Math.floor(viewport.width)}px`;
      canvas.style.height = `${Math.floor(viewport.height)}px`;

      const transform = outputScale !== 1 ? [outputScale, 0, 0, outputScale, 0, 0] : null;

      if (renderTaskRef.current) {
        try {
          renderTaskRef.current.cancel();
        } catch (e) {
          // ignore
        }
      }

      const renderContext = {
        canvasContext: context,
        viewport: viewport,
        transform: transform || undefined
      };

      renderTaskRef.current = page.render(renderContext);
      await renderTaskRef.current.promise;
    } catch (err: any) {
      if (err?.name !== 'RenderingCancelledException') {
        console.error('Error rendering page:', err);
      }
    }
  };

  useEffect(() => {
    if (!loading && !useFallbackIframe && numPages > 0) {
      renderPage(undefined, currentPage, scale, rotation);
    }
  }, [currentPage, scale, rotation]);

  const handlePrevPage = () => {
    if (currentPage > 1) setCurrentPage(prev => prev - 1);
  };

  const handleNextPage = () => {
    if (currentPage < numPages) setCurrentPage(prev => prev + 1);
  };

  const handleZoomIn = () => setScale(prev => Math.min(prev + 0.2, 3.0));
  const handleZoomOut = () => setScale(prev => Math.max(prev - 0.2, 0.5));
  const handleRotate = () => setRotation(prev => (prev + 90) % 360);

  return (
    <div className={`flex flex-col bg-slate-900 rounded-2xl overflow-hidden border border-slate-700 shadow-xl ${className}`}>
      {/* Header Toolbar */}
      <div className="bg-slate-900 text-white px-4 py-3 flex flex-wrap items-center justify-between gap-3 border-b border-slate-800 shrink-0">
        <div className="flex items-center gap-2 max-w-xs sm:max-w-md truncate">
          {isGDrive ? (
            <HardDrive className="w-4 h-4 text-emerald-400 shrink-0" />
          ) : (
            <FileText className="w-4 h-4 text-amber-400 shrink-0" />
          )}
          <span className="text-xs font-bold truncate text-slate-200" title={title}>
            {title}
          </span>
          {isGDrive && (
            <span className="px-2 py-0.5 rounded-full bg-emerald-500/20 text-emerald-300 text-[10px] font-extrabold border border-emerald-500/30 uppercase shrink-0">
              Google Drive Preview
            </span>
          )}
        </div>

        {/* Controls for Local/Canvas PDF */}
        {!useFallbackIframe && numPages > 0 && (
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-1 bg-slate-800 px-2 py-1 rounded-lg border border-slate-700 text-xs font-semibold">
              <button
                type="button"
                onClick={handlePrevPage}
                disabled={currentPage <= 1}
                className="p-1 text-slate-300 hover:text-white disabled:opacity-30 cursor-pointer transition"
                title="Halaman Sebelumnya"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>

              <span className="text-amber-400 font-extrabold px-1 text-[11px] min-w-[70px] text-center">
                {currentPage} / {numPages}
              </span>

              <button
                type="button"
                onClick={handleNextPage}
                disabled={currentPage >= numPages}
                className="p-1 text-slate-300 hover:text-white disabled:opacity-30 cursor-pointer transition"
                title="Halaman Selanjutnya"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>

            <div className="hidden sm:flex items-center gap-1 bg-slate-800 px-1.5 py-1 rounded-lg border border-slate-700">
              <button
                type="button"
                onClick={handleZoomOut}
                disabled={scale <= 0.6}
                className="p-1 text-slate-300 hover:text-white disabled:opacity-30 cursor-pointer transition"
                title="Perkecil Zoom"
              >
                <ZoomOut className="w-3.5 h-3.5" />
              </button>

              <span className="text-[10px] text-slate-400 font-mono font-bold w-10 text-center">
                {Math.round(scale * 100)}%
              </span>

              <button
                type="button"
                onClick={handleZoomIn}
                disabled={scale >= 2.8}
                className="p-1 text-slate-300 hover:text-white disabled:opacity-30 cursor-pointer transition"
                title="Perbesar Zoom"
              >
                <ZoomIn className="w-3.5 h-3.5" />
              </button>

              <button
                type="button"
                onClick={handleRotate}
                className="p-1 text-slate-300 hover:text-white cursor-pointer transition ml-1 border-l border-slate-700 pl-1.5"
                title="Putar Halaman"
              >
                <RotateCw className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex items-center gap-2">
          <a
            href={isGDrive ? gDriveDownloadUrl : url}
            target="_blank"
            rel="noreferrer"
            download
            className="px-3 py-1.5 bg-amber-600 hover:bg-amber-700 text-white rounded-lg text-xs font-bold transition flex items-center gap-1.5 shadow-xs cursor-pointer"
            title="Download PDF"
          >
            <Download className="w-3.5 h-3.5" />
            <span className="hidden sm:inline">Download</span>
          </a>

          <a
            href={isGDrive ? gDriveViewUrl : url}
            target="_blank"
            rel="noreferrer"
            className="p-1.5 bg-slate-800 hover:bg-slate-700 text-slate-300 hover:text-white rounded-lg transition cursor-pointer border border-slate-700"
            title="Buka Tab Baru / Google Drive"
          >
            <ExternalLink className="w-4 h-4" />
          </a>
        </div>
      </div>

      {/* Canvas Viewport or Fallback Container */}
      <div 
        ref={containerRef}
        style={{ height }}
        className="flex-1 bg-slate-950 overflow-auto p-2 sm:p-4 flex items-center justify-center relative select-none"
      >
        {loading && (
          <div className="flex flex-col items-center justify-center space-y-3 p-8 bg-slate-900/80 rounded-2xl border border-slate-800 backdrop-blur-xs">
            <Loader2 className="w-8 h-8 text-amber-500 animate-spin" />
            <span className="text-xs font-bold text-slate-300">Memuat Dokumen PDF...</span>
          </div>
        )}

        {!loading && !useFallbackIframe && (
          <div className="flex justify-center items-center my-auto transition-all duration-200">
            <canvas 
              ref={canvasRef} 
              className="shadow-2xl rounded-lg border border-slate-800 bg-white max-w-full object-contain"
            />
          </div>
        )}

        {!loading && useFallbackIframe && (
          <div className="w-full h-full flex flex-col">
            <iframe
              src={fallbackIframeUrl}
              title={title}
              allow="autoplay"
              className="w-full h-full rounded-xl border border-slate-800 bg-white shadow-lg"
            />
          </div>
        )}
      </div>

      {/* Footer Info Bar */}
      <div className="bg-slate-900 border-t border-slate-800 px-4 py-2 flex items-center justify-between text-[11px] text-slate-400">
        <span>
          {isGDrive 
            ? 'Dokumen dimuat via Google Drive Live Embed Preview.'
            : useFallbackIframe
            ? 'Dokumen dimuat via Universal PDF Viewer.'
            : 'Gunakan navigasi untuk berpindah halaman.'
          }
        </span>
        <span className="font-semibold text-slate-300">UT Safety Mandatory Training</span>
      </div>
    </div>
  );
};

